import React, { ReactElement, useContext, useEffect, useState } from 'react'
import styled from 'styled-components'
import {
    fetchEntityOptions,
    fetchUnitOptions,
    INTERVENTION_API,
    WELL_API,
    SECTION_API,
    RIG_TABLE_API,
    RUN_API,
    TUBULAR_API
} from './api/api'
import { ActivityChart } from './charts/activity/ActivityChart'
import { UnitsTypes } from './charts/chart-components'
import { InterventionChart } from './charts/intervention/InterventionChart'
import { PhaseChart } from './charts/phase/PhaseChart'
import { RigTableChart } from './charts/rigTable/RigTableChart'
import { RunChart } from './charts/run/RunChart'
import { TimelineChart } from './charts/timeline/TimelineChart'
import { getTemporalWidgetLeftRight, getTemporalWidgetDOM } from './charts/timeline/temporal-widget-dom-utils'
import { StaticDataContext } from './context/StaticDataContext'

export const StaticDataCharts = ({ width, height }: { width: number; height: number }): ReactElement => {
    const [padding, setPadding] = useState<{ left: number; right: number }>({ left: 3, right: 3 })

    const { liveTheme } = useContext(StaticDataContext)

    const memoizeEntityOptions = () =>
        Object.values([
            INTERVENTION_API,
            WELL_API,
            SECTION_API,
            RIG_TABLE_API,
            RUN_API,
            TUBULAR_API
        ]).forEach(entityApi => fetchEntityOptions(entityApi))
    const memoizeUnitOptions = () => Object.values(UnitsTypes).forEach(unitType => fetchUnitOptions(unitType))

    useEffect(() => {
        memoizeEntityOptions()
        memoizeUnitOptions()
        setPadding(getTemporalWidgetLeftRight())
    }, [])

    useEffect(() => {
        if (getTemporalWidgetDOM()) {
            setTimeout(() => {
                const DEFAULT_PATH = '.temporal-widget .highcharts-container > svg >'

                const crosshair = document.querySelector(`${DEFAULT_PATH} .highcharts-crosshair-thin`)
                const horizontalLines = document.querySelectorAll(
                    `${DEFAULT_PATH} g.highcharts-grid.highcharts-yaxis-grid > path`
                )
                const timeline = document.querySelectorAll(`${DEFAULT_PATH} g.highcharts-axis.highcharts-xaxis > path`)
                const timelineLabels = document.querySelectorAll(
                    `${DEFAULT_PATH} g.highcharts-axis-labels.highcharts-xaxis-labels > text`
                )
                const verticalLabels = document.querySelectorAll(
                    `${DEFAULT_PATH} g.highcharts-axis-labels.highcharts-yaxis-labels > text`
                )

                if (crosshair) {
                    crosshair?.setAttribute(
                        'style',
                        `
                            stroke: var(--gray-600);
                        `
                    )
                }
                if (horizontalLines?.length > 0) {
                    horizontalLines.forEach(element => element.setAttribute('style', 'stroke: var(--gray-300);'))
                }
                if (timeline?.length > 0) {
                    timeline.forEach(element => element.setAttribute('style', 'stroke: var(--gray-500);'))
                }
                if (timelineLabels?.length > 0) {
                    timelineLabels.forEach(element =>
                        element.setAttribute(
                            'style',
                            'color:var(--gray-900);cursor:default;font-size:11px;fill:var(--gray-900);'
                        )
                    )
                }
                if (verticalLabels?.length > 0) {
                    verticalLabels.forEach(element =>
                        element.setAttribute(
                            'style',
                            'color:var(--gray-800);cursor:default;font-size:11px;fill:var(--gray-800);'
                        )
                    )
                }
            }, 2000)
        }
    }, [liveTheme])

    return (
        <>
            <TimelineChart
                width={width}
                height={height}
                updateStatePadding={() => setPadding(getTemporalWidgetLeftRight())}
            />

            <StaticDataChartsContainer left={padding.left} right={padding.right}>
                <div className="charts-container">
                    <InterventionChart />
                    <PhaseChart />
                    <RigTableChart />
                    <RunChart />
                    <ActivityChart />
                </div>
            </StaticDataChartsContainer>
        </>
    )
}

const StaticDataChartsContainer = styled.div<{
    left: number
    right: number
}>`
    border-radius: 0px 4px 0px 4px;
    width: 100%;
    height: 100%;
    display: flex;
    padding-top: 19px;
    padding-right: ${({ right }) => right}px;
    padding-bottom: 19px;
    padding-left: ${({ left }) => left}px;
    margin-top: -35px;
    button {
        background-color: #37a3ef;
        border: none;
        border-radius: 3px;
        font-size: 15px;
        font-weight: 300;
        color: white;
        padding: 7px;
    }
    .charts-container {
        width: 100%;
        height: 100%;
        display: flex;
        flex-flow: column;
        justify-content: space-between;
        padding: 0px 3px 0px 2px;
        margin-bottom: 1rem;
        min-height: 111px;
        margin-top: 5px;
    }
`
